import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Edit, Trash2, ChevronUp, ChevronDown, Image as ImageIcon } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { dataService, AddOnOption, AddOnCategory } from '../../services/dataService';
import AddOnOptionModal from './AddOnOptionModal';

const AddOnOptions: React.FC = () => {
  const { tenantData } = useAuth();
  const [categories, setCategories] = useState<AddOnCategory[]>([]);
  const [options, setOptions] = useState<AddOnOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingOption, setEditingOption] = useState<AddOnOption | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());

  const loadData = useCallback(async () => {
    if (!tenantData?.id) return;
    
    try {
      setLoading(true);
      const [categoriesData, optionsData] = await Promise.all([
        dataService.getAddOnCategories(tenantData.id),
        dataService.getAllAddOnOptions(tenantData.id)
      ]);
      setCategories(categoriesData);
      setOptions(optionsData);
    } catch (error) {
      console.error('Error loading add-on data:', error);
      setError('Failed to load add-on data');
    } finally {
      setLoading(false);
    }
  }, [tenantData]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleAddOption = () => {
    setEditingOption(null);
    setShowModal(true);
  };

  const handleEditOption = (option: AddOnOption) => {
    setEditingOption(option);
    setShowModal(true);
  };

  const handleDeleteOption = async (optionId: string) => {
    if (!tenantData?.id) return;
    
    if (!window.confirm('Are you sure you want to delete this option?')) {
      return;
    }

    try {
      await dataService.deleteAddOnOption(tenantData.id, optionId);
      await loadData();
    } catch (error) {
      console.error('Error deleting option:', error);
      alert('Failed to delete option: ' + (error as Error).message);
    }
  };

  const handleModalClose = () => {
    setShowModal(false);
    setEditingOption(null);
  };

  const handleModalSuccess = () => {
    setShowModal(false);
    setEditingOption(null);
    loadData();
  };

  const toggleCategoryExpansion = (categoryId: string) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(categoryId)) {
      newExpanded.delete(categoryId);
    } else {
      newExpanded.add(categoryId);
    }
    setExpandedCategories(newExpanded);
  };

  const moveOption = async (optionId: string, direction: 'up' | 'down') => {
    if (!tenantData?.id) return;
    
    const currentOption = options.find(opt => opt.id === optionId);
    if (!currentOption) return;
    
    const categoryOptions = options.filter(opt => opt.categoryId === currentOption.categoryId);
    const currentIndex = categoryOptions.findIndex(opt => opt.id === optionId);
    if (currentIndex === -1) return;
    
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (newIndex < 0 || newIndex >= categoryOptions.length) return;
    
    const newOptions = [...categoryOptions];
    const [movedOption] = newOptions.splice(currentIndex, 1);
    newOptions.splice(newIndex, 0, movedOption);
    
    // Update display order for all affected options
    const updatePromises = newOptions.map((opt, index) => {
      if (opt.displayOrder !== index) {
        return dataService.updateAddOnOption(tenantData.id, opt.id!, {
          displayOrder: index
        });
      }
      return Promise.resolve();
    });
    
    try {
      await Promise.all(updatePromises);
      await loadData();
    } catch (error) {
      console.error('Error reordering options:', error);
      alert('Failed to reorder options');
    }
  };

  const getFilteredOptions = () => {
    if (selectedCategory === 'all') {
      return options;
    }
    return options.filter(option => option.categoryId === selectedCategory);
  };

  const getOptionsByCategory = (categoryId: string) => {
    return options.filter(option => option.categoryId === categoryId);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Add-On Options</h2>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Manage individual options within add-on categories
          </p>
        </div>
        <button
          onClick={handleAddOption}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
        >
          <Plus className="w-4 h-4" />
          <span>Add Option</span>
        </button>
      </div>

      {/* Category Filter */}
      <div className="flex items-center space-x-4">
        <label htmlFor="categoryFilter" className="text-sm font-medium text-gray-700 dark:text-gray-300">
          Filter by Category:
        </label>
        <select
          id="categoryFilter"
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
          className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
        >
          <option value="all">All Categories</option>
          {categories.map(category => (
            <option key={category.id} value={category.id}>
              {category.name}
            </option>
          ))}
        </select>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-red-800 dark:text-red-200">{error}</p>
        </div>
      )}

      {/* Options List */}
      {getFilteredOptions().length === 0 ? (
        <div className="text-center py-12">
          <div className="text-gray-400 dark:text-gray-500 mb-4">
            <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No add-on options yet</h3>
          <p className="text-gray-500 dark:text-gray-400 mb-4">
            {selectedCategory === 'all' 
              ? 'Create your first add-on option to start offering customization choices to customers.'
              : 'No options in this category yet. Add some options to get started.'
            }
          </p>
          <button
            onClick={handleAddOption}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
          >
            Create First Option
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {categories
            .filter(category => selectedCategory === 'all' || category.id === selectedCategory)
            .map(category => {
              const categoryOptions = getOptionsByCategory(category.id!);
              if (categoryOptions.length === 0) return null;
              
              return (
                <div key={category.id} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-lg font-medium text-gray-900 dark:text-white">{category.name}</h3>
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        {categoryOptions.length} option{categoryOptions.length !== 1 ? 's' : ''}
                      </span>
                    </div>
                    
                    <div className="space-y-2">
                      {categoryOptions
                        .sort((a, b) => a.displayOrder - b.displayOrder)
                        .map((option, index) => (
                          <div key={option.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                            <div className="flex items-center space-x-3">
                              <div className="flex items-center space-x-2">
                                <button
                                  onClick={() => moveOption(option.id!, 'up')}
                                  disabled={index === 0}
                                  className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                  title="Move up"
                                >
                                  <ChevronUp className="w-3 h-3" />
                                </button>
                                <button
                                  onClick={() => moveOption(option.id!, 'down')}
                                  disabled={index === categoryOptions.length - 1}
                                  className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                  title="Move down"
                                >
                                  <ChevronDown className="w-3 h-3" />
                                </button>
                              </div>
                              
                              <div className="flex items-center space-x-3">
                                {option.image ? (
                                  <img 
                                    src={option.image} 
                                    alt={option.name}
                                    className="w-8 h-8 rounded object-cover"
                                  />
                                ) : (
                                  <div className="w-8 h-8 bg-gray-200 dark:bg-gray-600 rounded flex items-center justify-center">
                                    <ImageIcon className="w-4 h-4 text-gray-400" />
                                  </div>
                                )}
                                
                                <div>
                                  <h4 className="font-medium text-gray-900 dark:text-white">{option.name}</h4>
                                  {option.description && (
                                    <p className="text-sm text-gray-500 dark:text-gray-400">{option.description}</p>
                                  )}
                                </div>
                              </div>
                            </div>
                            
                            <div className="flex items-center space-x-3">
                              <span className="text-sm font-medium text-green-600 dark:text-green-400">
                                +₹{option.price}
                              </span>
                              
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                option.isAvailable 
                                  ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200' 
                                  : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200'
                              }`}>
                                {option.isAvailable ? 'Available' : 'Unavailable'}
                              </span>
                              
                              <button
                                onClick={() => handleEditOption(option)}
                                className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600 rounded-lg transition-colors"
                                title="Edit option"
                              >
                                <Edit className="w-4 h-4" />
                              </button>
                              
                              <button
                                onClick={() => handleDeleteOption(option.id!)}
                                className="p-2 text-red-400 hover:text-red-600 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                                title="Delete option"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                </div>
              );
            })}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <AddOnOptionModal
          option={editingOption}
          categories={categories}
          onClose={handleModalClose}
          onSuccess={handleModalSuccess}
        />
      )}
    </div>
  );
};

export default AddOnOptions;

